import { useTranslation } from "react-i18next";
import { availableLanguages } from "../../../i18n/languages";
import styled from "styled-components";
import { Topic, TopicEntity } from "~/types";
import { AppContext } from "~/contexts/AppContext";
import { useContext, useEffect } from "react";

const LanguageSelectorContainer = styled.div`
    position: absolute;
    left: 37px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 10;
`;

const LanguageLabel = styled.div`
    color: white;
    font-size: 12px;
    opacity: 0.8;
    margin-bottom: 8px;
    padding: 4px;
`;

const LanguageList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const LanguageItem = styled.div<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;

    opacity: ${props => props.isActive ? '1' : '0.6'};
    background-color: ${props => props.isActive ? '#fff5' : '' };

`;

const FlagImage = styled.img`
    width: 24px;
    height: 18px;
    object-fit: cover;
`;

const LanguageCode = styled.span`
    color: white;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
`;

interface LanguageSelectorProps {
    topic: Topic | null;
    entity: TopicEntity | null;
    languages: Array<{ code: string; label: string; flag: string, displayName: string }>;
    onLanguageChange?: () => void;
}

const POPULARITY_ORDER = ['en','es','fr','pt','de','tr','it','ro','bg','mt'] as const;
const rank = (code: string) => {
    const i = POPULARITY_ORDER.indexOf(code as any);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

const LanguageSelector: React.FC<LanguageSelectorProps> = (props) => {
    const { i18n, t } = useTranslation();
    const { recordAnalyticsEvent } = useContext(AppContext)!;
    
    const currentLanguage = i18n.language;

    useEffect(() => {
        recordAnalyticsEvent(
            'language-selector-ab-test-2025-11',
            'open-language-selector-NEW',
            props.topic?.id,
            props.entity?.id
        );
    }, []);

    const handleLanguageChange = (langCode: string) => {
        i18n.changeLanguage(langCode);
        if (props.onLanguageChange) {
            props.onLanguageChange();
        }
    };

    return (
        <LanguageSelectorContainer>
            <LanguageLabel>
                {t('choose')}
                <br />
                {t('language')}
            </LanguageLabel>
            <LanguageList>
                {props.languages.map((lang) => (
                    <LanguageItem
                        key={lang.code}
                        isActive={currentLanguage === lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                    >
                        <FlagImage 
                            src={`/images/flags/${lang.code}.jpeg`} 
                            alt={lang.label}
                        />
                        <LanguageCode>{lang.label}</LanguageCode>
                    </LanguageItem>
                ))}
            </LanguageList>
        </LanguageSelectorContainer>
    );
};

interface LanguageSelectorWrapperProps {
    topic: Topic | null;
    entity: TopicEntity | null;
    altLngs: string[];
    onLanguageChange?: () => void;
}

const LanguageSelectorWrapper: React.FC<LanguageSelectorWrapperProps> = (props) => {
    if(props.topic === null) {
        return null;
    }
    
    const defaultLanguage = props.topic.defaultLanguage;

    const topicLangCodes = Array.from(new Set([defaultLanguage, ...props.altLngs]));

    const sortedCodes = topicLangCodes.sort((a, b) => {
        if (a === defaultLanguage && b !== defaultLanguage) return -1;
        if (b === defaultLanguage && a !== defaultLanguage) return 1;

        if (defaultLanguage !== 'en') {
            if (a === 'en' && b !== 'en') return -1;
            if (b === 'en' && a !== 'en') return 1;
        }

        return rank(a) - rank(b);
    });

    const languages = sortedCodes
    .map(code => availableLanguages[code as keyof typeof availableLanguages]);
    
    if(languages.length < 2) {
        return null;
    }

    return (
        <LanguageSelector
            topic={props.topic}
            entity={props.entity}
            languages={languages}
            onLanguageChange={props.onLanguageChange}
        />
    );
};

export default LanguageSelectorWrapper;